import { Table, Typography } from "antd";
import React, { forwardRef, useCallback, useImperativeHandle, useMemo, useState } from "react";
import axios from "api/axios";
import { useDpatIndicatorDataSource } from "hooks/useDpatIndicatorDataSource";
import {
  calculatePercentage,
  formatDataGeneral,
  getAttributeValue,
  getFileLinkIfExist,
} from "utils/utils";
import { renderDpatEvidenceLink, scoreOnlyIfDocumentLinksComplete } from "utils/renderDpatEvidenceLink";
import { filterRecordsByDpatYearAttribute, isDpatScoreRelaxationExempt } from "utils/dpatScoreRelaxation";
import { documentsColumn } from "utils/tableColums";
import DpatSubIndicatorSection from "../setup/DpatSubIndicatorSection";
import DpatIndicatorCmsIntro from "../setup/DpatIndicatorCmsIntro";
import MaxScoreDisplay from "../setup/MaxScoreDisplay";
import ActualScoreDisplay from "../setup/ActualScoreDisplay";
import IndicatorScoreSection from "../setup/IndicatorScoreSection";
import Comment from "components/Comments";

const DOCUMENT_HUB_FILE_ELEMENT = "MjPmcbueEdU";
const IGF_PROGRAM = "dYNmYGtArrK";
const IGF_YEAR_ATTRIBUTE = "pWkYqcKukAY";
const IGF_AMOUNT_COLLECTED_ATTRIBUTE = "nlZbT98zSio";
const AAP_BUDGET_ALLOCATED_ATTRIBUTE = "mdO1ngv0Qt0";
const TRACKER_ENTITIES_PAGE_SIZE = 5000;
const TRACKER_ENTITY_FIELDS =
  "trackedEntity,attributes[attribute,displayName,value]";

function fileUrl(eventId, dataElementUid) {
  return `https://dddp.gov.gh/api/events/files?eventUid=${eventId}&dataElementUid=${dataElementUid}`;
}

function parseAmount(value) {
  if (value == null || value === "" || value === "N/A") {
    return 0;
  }

  const parsed = parseFloat(String(value).replace(/,/g, ""));
  return Number.isFinite(parsed) ? parsed : 0;
}

function formatGhanaCedis(amount) {
  const value = parseAmount(amount);
  return `GH₵ ${value.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function getAttributeWithFallback(unit, displayNames, attributeId) {
  for (const name of displayNames) {
    const value = getAttributeValue(name, unit);
    if (value && value !== "N/A") {
      return value;
    }
  }

  const matched = unit?.attributes?.find((attr) => attr.attribute === attributeId);
  const value = matched?.value;
  return value == null || value === "" ? "N/A" : value;
}

function getIgfYear(record) {
  return getAttributeWithFallback(
    record,
    ["Year", "Select Year", "DPAT | Year"],
    IGF_YEAR_ATTRIBUTE,
  );
}

function getIgfAmountCollected(record) {
  return getAttributeWithFallback(
    record,
    [
      "Amount Collected",
      "Amt Collected",
      "DPAT | Amount Collected",
      "Amount collected",
    ],
    IGF_AMOUNT_COLLECTED_ATTRIBUTE,
  );
}

function getPlanBudgetAllocated(plan) {
  return getAttributeWithFallback(
    plan,
    ["Budget Allocated", "Budget Allocated(Ghana cedis)", "DPAT | Budget Allocated"],
    AAP_BUDGET_ALLOCATED_ATTRIBUTE,
  );
}

function filterIgfRecordsByYear(records = [], year) {
  if (isDpatScoreRelaxationExempt()) {
    return filterRecordsByDpatYearAttribute(records, year);
  }
  const yearStr = String(year);
  if (!yearStr || yearStr === "undefined" || yearStr === "null") {
    return records;
  }

  const displayNameMatches = [
    formatDataGeneral(records, "Year", yearStr),
    formatDataGeneral(records, "Select Year", yearStr),
    formatDataGeneral(records, "DPAT | Year", yearStr),
  ].find((matches) => matches?.length);

  if (displayNameMatches?.length) {
    return displayNameMatches;
  }

  return records.filter((record) => String(getIgfYear(record)) === yearStr);
}

async function fetchIgfRecordsForYear(orgUnit, year) {
  const params = {
    orgUnit,
    program: IGF_PROGRAM,
    pageSize: TRACKER_ENTITIES_PAGE_SIZE,
    fields: TRACKER_ENTITY_FIELDS,
  };
  if (!isDpatScoreRelaxationExempt()) {
    params.filter = `${IGF_YEAR_ATTRIBUTE}:EQ:${year}`;
  }
  const response = await axios.get("/tracker/trackedEntities", { params });

  const instances = response?.data?.instances || [];
  const filtered = filterIgfRecordsByYear(instances, year);
  return filtered.length ? filtered : instances;
}

function buildSanitationExpenditureData({ plans, ifgRecords, year }) {
  const aapPlans = plans?.aap ?? plans ?? [];

  const sanitationPlans = formatDataGeneral(
    formatDataGeneral(aapPlans, "Sector", "Sanitation") || [],
    "Year",
    `${year}`,
  ) || [];

  let totalSpent = 0;
  sanitationPlans.forEach((plan) => {
    totalSpent += parseAmount(getPlanBudgetAllocated(plan));
  });

  const yearIgfRecords = filterIgfRecordsByYear(ifgRecords, year);
  let totalCollected = 0;
  yearIgfRecords.forEach((record) => {
    totalCollected += parseAmount(getIgfAmountCollected(record));
  });

  const percentage = Number(calculatePercentage(totalSpent, totalCollected).toFixed(2));

  return {
    key: "sanitation-igf-expenditure",
    ifgCollected: totalCollected,
    igfSpentOnSanitation: totalSpent,
    percentage,
  };
}

function buildIgfReportDocLinks(documents) {
  const documentData = documents?.data;
  const documentReports = documents?.reports;
  const igfReportDocs = formatDataGeneral(
    documentData,
    "DPAT | M&E Tool Type",
    "Internally Generated Fund Report (IGF)",
  ) || [];

  let igfReportLink = "";
  if (igfReportDocs.length > 0) {
    igfReportLink = getFileLinkIfExist(
      documentReports,
      DOCUMENT_HUB_FILE_ELEMENT,
      igfReportDocs[0]?.trackedEntity,
    );
  }

  return [
    {
      title: "Internally Generated Fund Report (IGF)",
      link: igfReportLink
        ? fileUrl(igfReportLink, DOCUMENT_HUB_FILE_ELEMENT)
        : "Not Uploaded",
    },
  ];
}

function normalizeExpenditureRow(row) {
  if (!row) {
    return null;
  }

  const collected = parseAmount(row.ifgCollected);
  const spent = parseAmount(row.igfSpentOnSanitation);
  const percentage = Number.isFinite(Number(row.percentage))
    ? Number(Number(row.percentage).toFixed(2))
    : Number(calculatePercentage(spent, collected).toFixed(2));

  return {
    key: row.key || "sanitation-igf-expenditure",
    ifgCollected: collected,
    igfSpentOnSanitation: spent,
    percentage,
  };
}

const SanitationServices = forwardRef(({
  year,
  plans,
  ifg,
  districtId,
  documents,
  hideComment,
  savedAssessmentData,
  assessmentStartLoading,
  isAssessmentStarted,
}, ref) => {
  const { Title, Paragraph } = Typography;
  const [score, setScore] = useState(0);
  const [maxScore] = useState(3);
  const [expenditureRow, setExpenditureRow] = useState(null);
  const [documentLinks, setDocumentLinks] = useState([]);

  const resolvedYear =
    year != null && typeof year === "object" && !Array.isArray(year)
      ? year.value ?? year.label
      : year;
  const analyticsYear = resolvedYear ?? new Date().getFullYear();
  const ouId =
    typeof districtId === "string"
      ? districtId
      : districtId?.value?.id ?? districtId?.id ?? "";

  const liveDocumentLinks = useMemo(
    () => buildIgfReportDocLinks(documents),
    [documents],
  );

  const loadExpenditureData = useCallback(async () => {
    setDocumentLinks(liveDocumentLinks);

    let ifgRecords = ifg?.data ?? [];
    if (ouId) {
      try {
        ifgRecords = await fetchIgfRecordsForYear(ouId, analyticsYear);
      } catch (err) {
        console.error("SanitationServices IGF fetch failed:", err);
        ifgRecords = filterIgfRecordsByYear(ifg?.data ?? [], analyticsYear);
      }
    } else {
      ifgRecords = filterIgfRecordsByYear(ifgRecords, analyticsYear);
    }

    const row = buildSanitationExpenditureData({
      plans,
      ifgRecords,
      year: analyticsYear,
    });
    setExpenditureRow(row);
    const rawScore = row.percentage >= 20 ? 3 : 0;
    setScore(scoreOnlyIfDocumentLinksComplete(liveDocumentLinks, rawScore));
  }, [ouId, analyticsYear, plans, ifg, liveDocumentLinks]);

  useDpatIndicatorDataSource({
    isAssessmentStarted,
    savedAssessmentData,
    assessmentStartLoading,
    loadFromDhis2: loadExpenditureData,
    applySnapshot: (snap) => {
      if (snap.sanitationProvidersData?.length) {
        setExpenditureRow(normalizeExpenditureRow(snap.sanitationProvidersData[0]));
      }
      if (snap.links) setDocumentLinks(snap.links);
      if (snap.score != null) setScore(snap.score);
    },
    deps: [ouId, analyticsYear, plans, ifg, documents, loadExpenditureData],
  });

  const rawExpenditureRow = useMemo(
    () => normalizeExpenditureRow(expenditureRow),
    [expenditureRow],
  );

  const expenditureTableData = useMemo(() => {
    if (!rawExpenditureRow) {
      return [];
    }

    return [{
      ...rawExpenditureRow,
      ifgCollectedDisplay: formatGhanaCedis(rawExpenditureRow.ifgCollected),
      igfSpentOnSanitationDisplay: formatGhanaCedis(rawExpenditureRow.igfSpentOnSanitation),
      percentageDisplay: `${rawExpenditureRow.percentage.toFixed(2)}%`,
    }];
  }, [rawExpenditureRow]);

  const displayDocumentLinks = isAssessmentStarted && savedAssessmentData?.links?.length
    ? savedAssessmentData.links
    : documentLinks.length
      ? documentLinks
      : liveDocumentLinks;
  const displayPercentage = rawExpenditureRow?.percentage ?? 0;

  useImperativeHandle(ref, () => ({
    getData: () => ({
      indicator: "SDI2",
      area: "Basic/ Social Services",
      maxScore,
      sanitationProvidersData: rawExpenditureRow ? [rawExpenditureRow] : [],
      links: displayDocumentLinks,
      score,
      percentage: displayPercentage,
    }),
  }));

  const DPAT_SLUG = "sdi2.0-2.3";

  const serviceProvidersColumn = [
    {
      title: `Total IGF collected for the ${analyticsYear} (A)`,
      dataIndex: "ifgCollectedDisplay",
      key: "ifgCollected",
    },
    {
      title: "Total IGF spent on sanitation improvement services (B)",
      dataIndex: "igfSpentOnSanitationDisplay",
      key: "igfSpentOnSanitation",
    },
    {
      title: "% of IGF spent on sanitation improvement services (B/A x 100)",
      dataIndex: "percentageDisplay",
      key: "percentage",
    },
  ];

  const documentTableColumns = useMemo(
    () => documentsColumn.map((col) =>
      col.dataIndex === "link"
        ? {
          ...col,
          render: (value) => renderDpatEvidenceLink(value, "View Document"),
        }
        : col
    ),
    [],
  );

  return (
    <Comment
      data={expenditureTableData}
      year={analyticsYear}
      districtId={ouId}
      tableCommentedId={`sdi2.0-2.3-${analyticsYear}`}
      hideComment={hideComment}
    >
      {({ renderCommentFeedbackFooter }) => (
          <DpatSubIndicatorSection code={DPAT_SLUG}>
          <DpatIndicatorCmsIntro
            year={analyticsYear}
            slug={DPAT_SLUG}
            mainTitleStyle={{ marginTop: "30px" }}
          />

          <IndicatorScoreSection>
            <MaxScoreDisplay score={maxScore} />
            <ActualScoreDisplay slug={DPAT_SLUG} label="SDI 2.0-2.3i Actual Score" score={score} />
          </IndicatorScoreSection>

          <Title level={5} style={{ marginTop: "30px" }}>I. Evidence of IGF expenditure on sanitation improvement services</Title>
          {expenditureTableData.length > 0 && (
            <Table
              columns={serviceProvidersColumn}
              dataSource={expenditureTableData}
              rowKey="key"
              pagination={false}
              bordered
            />
          )}

          <Title level={5} style={{ marginTop: "20px" }}>Evidence of IGF expenditure (Document Hub)</Title>
          <Table
            columns={documentTableColumns}
            dataSource={displayDocumentLinks}
            rowKey="title"
            pagination={false}
            bordered
          />

          <Title level={5} style={{ marginTop: "30px" }}>Conclusion:</Title>
          <Paragraph>
            <ol>
              <li type="i">
                Total IGF collected for {analyticsYear} (A) is{" "}
                {formatGhanaCedis(rawExpenditureRow?.ifgCollected ?? 0)}.
              </li>
              <li className="py-1" type="i">
                Total IGF spent on sanitation improvement services (B) is{" "}
                {formatGhanaCedis(rawExpenditureRow?.igfSpentOnSanitation ?? 0)}.
              </li>
              <li className="py-1" type="i">
                {displayPercentage.toFixed(2)}% of IGF was spent on sanitation improvement services
                in {analyticsYear} (calculated as B/A x 100 ={" "}
                {formatGhanaCedis(rawExpenditureRow?.igfSpentOnSanitation ?? 0)} /{" "}
                {formatGhanaCedis(rawExpenditureRow?.ifgCollected ?? 0)} x 100).
              </li>
            </ol>
          </Paragraph>

            {!hideComment && renderCommentFeedbackFooter()}
          </DpatSubIndicatorSection>
      )}
    </Comment>
  );
});

export default SanitationServices;
